import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import { type CollectionMall } from './collectionMalls';
import {
  newRowKey,
  type CollectionField,
  type ConsentItem,
  type ProductCollectionConfig,
} from './types';

/**
 * ID41 상품 커스텀 폼 + 가변 동의 항목 GET/PUT. (항목2: 4몰별 분리.)
 * BE 계약(be-catalog, 통합 collection-form, per-mall):
 *   GET  `/admin/v1/products/{productId}/collection-form?mall=<BCP-47>` → {productId, fields[], consentTerms[]}
 *   PUT  `/admin/v1/products/{productId}/collection-form?mall=<BCP-47>`  body {fields[], consents[]} 전체교체 upsert
 * mall 쿼리파라미터로 몰별 독립 폼(탭 전환=mall 변경). body 구조는 mall 차원과 무관하게 불변
 * (toWire/fromWire 매핑 유지). generated OpenAPI 미반영 → adminFetch 로 직접
 * (카테고리 훅·order_form_fields 선례).
 *
 * ⚠ 키 비대칭(이벤트 패턴): GET 응답 동의 배열=`consentTerms`, PUT body=`consents`.
 *    field: {fieldKey, label, fieldType, required, sensitive, sortOrder, options}.
 *    consent: {title, contentHtml, linkUrl, required, sortOrder}.
 */
interface FieldWire {
  fieldKey?: string | null;
  label?: string | null;
  fieldType?: string | null;
  required?: boolean | null;
  sensitive?: boolean | null;
  sortOrder?: number | null;
  options?: string[] | null;
}
interface ConsentWire {
  // GET seed 키. (PUT body 는 contentHtml 동일, title/linkUrl/required/sortOrder 동일)
  title?: string | null;
  contentHtml?: string | null;
  linkUrl?: string | null;
  required?: boolean | null;
  sortOrder?: number | null;
}
interface CollectionEnvelope {
  success: boolean;
  data?: { fields?: FieldWire[] | null; consentTerms?: ConsentWire[] | null } | null;
}

// fieldType 수신 허용 superset (BO UI 는 앞 5종만 생성, 나머지는 graceful 보존).
const VALID_TYPES = new Set([
  'TEXT',
  'TEXTAREA',
  'RADIO',
  'CHECKBOX',
  'SELECT',
  'PHONE',
  'EMAIL',
  'DATE',
  'ACCOUNT',
]);

const fieldFromWire = (w: FieldWire): CollectionField => ({
  key: w.fieldKey ?? newRowKey('field'),
  label: w.label ?? '',
  type: (w.fieldType && VALID_TYPES.has(w.fieldType) ? w.fieldType : 'TEXT') as CollectionField['type'],
  required: w.required ?? false,
  options: w.options ?? [],
  sensitive: w.sensitive ?? false,
});

const consentFromWire = (w: ConsentWire): ConsentItem => ({
  key: newRowKey('consent'),
  title: w.title ?? '',
  content: w.contentHtml ?? '',
  linkUrl: w.linkUrl ?? '',
  required: w.required ?? true,
});

/** per-mall GET/PUT 경로 — `?mall=<BCP-47>`. 테스트용 export. */
export const collectionPath = (productId: string, mall: CollectionMall) =>
  `/admin/v1/products/${productId}/collection-form?mall=${encodeURIComponent(mall)}`;

const collectionKey = (productId: string, mall: CollectionMall) =>
  ['admin', 'products', productId, 'collection-form', mall] as const;

export const useProductCollectionQuery = (
  productId: string | undefined,
  mall: CollectionMall,
) =>
  useQuery<ProductCollectionConfig>({
    queryKey: collectionKey(productId ?? '', mall),
    enabled: Boolean(productId),
    queryFn: async () => {
      const res = await adminFetch(collectionPath(productId as string, mall));
      if (res.status === 404) return { fields: [], consents: [] };
      if (!res.ok) throw new Error(`collection-form fetch failed: ${res.status}`);
      const env = (await res.json()) as CollectionEnvelope;
      return collectionFromWire(env.data);
    },
  });

/** GET 응답 data → 폼 config. consentTerms(GET) → consents(form). 테스트용 export. */
export const collectionFromWire = (
  data: { fields?: FieldWire[] | null; consentTerms?: ConsentWire[] | null } | null | undefined,
): ProductCollectionConfig => ({
  fields: (data?.fields ?? []).map(fieldFromWire),
  consents: (data?.consentTerms ?? []).map(consentFromWire),
});

/**
 * PUT 본문 — field.key 를 fieldKey(상품 내 unique)로 전송, sortOrder=배열 순서.
 * 동의 배열은 요청 키가 `consents`(GET 의 consentTerms 와 비대칭), content→contentHtml. 테스트용 export.
 */
export const collectionToWire = (config: ProductCollectionConfig) => ({
  fields: config.fields.map((f, i) => ({
    fieldKey: f.key,
    label: f.label,
    fieldType: f.type,
    required: f.required,
    sensitive: f.sensitive,
    sortOrder: i,
    options: f.options,
  })),
  consents: config.consents.map((c, i) => ({
    title: c.title,
    contentHtml: c.content,
    linkUrl: c.linkUrl,
    required: c.required,
    sortOrder: i,
  })),
});

export interface SaveCollectionInput {
  productId: string;
  /** 저장 대상 몰(per-mall 분리 저장). */
  mall: CollectionMall;
  config: ProductCollectionConfig;
}

/**
 * 커스텀 폼/동의 저장(몰별). productId 를 mutate 시점에 받는다 — 생성(create) 흐름은
 * 서버가 부여한 새 productId 를 onSuccess 콜백에서야 알 수 있기 때문. mall 도 함께 받아
 * `?mall=` per-mall PUT 으로 보낸다.
 */
export const useSaveProductCollectionMutation = () => {
  const qc = useQueryClient();
  return useMutation<void, Error, SaveCollectionInput>({
    mutationFn: async ({ productId, mall, config }) => {
      const p = collectionPath(productId, mall);
      const body = collectionToWire(config);
      const res = await adminFetch(p, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', p, body) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`collection-form save failed: ${res.status}`);
    },
    onSuccess: (_v, { productId, mall }) =>
      qc.invalidateQueries({ queryKey: collectionKey(productId, mall) }),
  });
};
